<template>
  <div class="DatoStructuredText">
    <StructuredText
      :data="content"
      :render-block="renderBlock"
      :render-link-to-record="renderLinkToRecord"
      :custom-node-rules="customRules"
    />
  </div>
</template>

<script>
import { StructuredText, renderRule } from 'vue-datocms'
import { isParagraph, isLink, isHeading } from 'datocms-structured-text-utils'
import {
  VisionLink,
  SingleImage,
  TextBackground,
  HighlightHeading,
  DialogStack,
  DialogComponent,
  DialogTextSmall,
  PrimaryLineLink,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  SecondaryLineLink,
  OutlineButton,
} from '#components'

export default {
  name: 'DatoStructuredText',

  components: {
    StructuredText,
    /* eslint-disable vue/no-unused-components */
    PrimaryLineLink,
    PrimaryButton,
    SecondaryButton,
    TertiaryButton,
    SecondaryLineLink,
    OutlineButton,
    /* eslint-enable vue/no-unused-components */
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
    copySize: {
      type: String,
      default: 'body',
      validator: (value) => ['body', 'body-sm'].includes(value),
    },
    ctaShape: {
      type: String,
      default: 'rounded',
    },
    ctaAlign: {
      type: String,
      default: 'center',
    },
    ctaBaseCopySize: {
      type: String,
      default: 'h4',
    },
    ctaGrow: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { resolveLocalePath } = useUrlBuilder()

    const sidebarStore = useSidebarStore()
    function openSidebarPage(slug) {
      sidebarStore.setActiveSidebar(slug)
    }

    return { resolveLocalePath, openSidebarPage }
  },

  computed: {
    customRules() {
      return [
        renderRule(
          isHeading,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            if (node.level === 1)
              return h(
                `h${node.level}`,
                {
                  key,
                  class: `DatoStructuredText__heading t-h${node.level}-md`,
                },
                children
              )
            return h(
              `h${node.level}`,
              { key, class: `DatoStructuredText__heading t-h${node.level}` },
              children
            )
          }
        ),
        renderRule(
          isParagraph,
          ({ adapter: { renderNode: h }, children, key }) => {
            return h(
              'p',
              {
                key,
                class:
                  'DatoStructuredText__body' +
                  ' ' +
                  (this.copySize === 'body' ? 't-body' : 't-body-sm'),
              },
              children
            )
          }
        ),
        renderRule(
          isLink,
          ({ node, children, key }) => {
            return h(
              VisionLink,
              {
                key,
                lineLink: true,
                size: 'inherit',
                to: this.resolveLocalePath(node.url),
                display: 'inline-block',
              },
              () => children
            )
          }
        ),
      ]
    },
  },

  methods: {
    renderLinkToRecord({ record, children }) {
      if (record.__typename === 'SidebarPageRecord') {
        return h(
          PrimaryLineLink,
          {
            isButton: true,
            size: 'inherit',
            onClick: (e) => {
              e.preventDefault()
              this.openSidebarPage(record.slug)
            },
          },
          () => children
        )
      }
    },
    renderBlock({ record }) {
      const isFirst =
        this.content?.value?.document?.children?.findIndex(
          (item) => item?.type === 'block' && item?.item === record.id
        ) === 0

      const inlineCTAComponents = {
        PrimaryButton,
        SecondaryButton,
        TertiaryButton,
        PrimaryLineLink,
        SecondaryLineLink,
        OutlineButton,
      }

      const $emit = this.$emit // We need to ref this where it might not be available

      const widthSuffix = record.widthMode === 'percentage' ? '%' : 'px'

      switch (record.__typename) {
        case 'SingleImageRecord':
          return h(SingleImage, {
            class: { 'is-first': isFirst, 'is-block': true },
            image: record.image,
            maxWidth: `${record.maxWidth}${widthSuffix}`,
            minWidth: record.minWidth ? `${record.minWidth}${widthSuffix}` : undefined,
          })
        case 'TextWithBackgroundRecord':
          return h(TextBackground, {
            class: { 'is-first': isFirst },
            id: record.id,
            title: record.title,
            subtitle: record.subtitle,
            copy: record.copy,
            background: record.background,
            mobileBackground: record.mobileBackground,
            gradientOverlay: record.gradientOverlay,
          })
        case 'HighlighterHeadingRecord':
          return h(HighlightHeading, {
            heading: record,
            background: 'light',
          })
        case 'DialogStackRecord':
          return h(DialogStack, {
            heading: record.heading,
            subheading: record.subheading,
          })
        case 'DialogComponentRecord':
          return h(DialogComponent, {
            componentName: record.componentName,
          })
        case 'TextSmallRecord':
          return h(DialogTextSmall, {
            text: record.text,
          })
        case 'InlineCtaRecord':
          if (!inlineCTAComponents[record.style]) return null
          return h(
            inlineCTAComponents[record.style],
            {
              shape: this.ctaShape,
              align: this.ctaAlign,
              baseCopySize: this.ctaBaseCopySize,
              grow: this.ctaGrow,
              onClick() {
                if (record.target?.startsWith('@')) {
                  $emit(record.target.replace('@', '')) // Emit the event name
                }
              },
            },
            () => record.copy
          )
        default: {
          return null
        }
      }
    },
  },
}
</script>

<style lang="postcss">
.DatoStructuredText {
  ol {
    @apply mb-md;
    list-style-type: decimal;
    list-style-position: inside;

    li {
      @apply pb-xs;

      &:last-of-type {
        @apply pb-0;
      }

      p {
        padding-left: 4px;
        display: inline;
      }
    }
  }

  ul {
    @apply mb-md ml-4;

    list-style-type: disc;
    list-style-position: outside;

    li {
      @apply pb-xs;

      &:last-of-type {
        @apply pb-0;
      }

      ul {
        @apply pl-4 list-disc;
      }
    }
  }

  ul li p + ul {
    @apply -mt-sm;
  }

  p {
    @apply mb-sm pb-xxs md:mb-md md:pb-0;

    &:last-child {
      @apply mb-0;
    }

    .LineLink--inherit {
      @apply pb-0;
    }
  }

  h2 {
    @apply mb-4;
  }

  h3 {
    @apply mb-3;
  }

  h4 {
    @apply mb-2;
  }

  h5 {
    @apply mb-1;
  }
}
</style>
